import videoCallConstants from "@data/constants/videoCallConstants"
import {liveServiceV2} from "@data/services/liveServiceV2"
import {apiConstants} from "../../../constants/apiConstants"
import {authConstants} from "../../../constants/authConstants"
import {liveModeConstants} from "../../../constants/liveModeConstants"
import {authService} from "../../../services/authService"
import {liveService} from "../../../services/liveService"

const initialState = {
    miniMode: false,
    focusedMode: true,
    isLiveModeEnabled: false,
    liveSessionId: null,
    // Le jour local de l'entrée dans le live : au démarrage, seul le live entré aujourd'hui se
    // rouvre (`liveMemory`).
    enteredOn: null,
    // La clé de cette entrée : l'adresse du live au web (`/live/<clé>`), jamais l'id de la séance.
    entryKey: null,
    // Le débordement du tuteur vu de la salle, relu à chaque poll — jamais persisté
    // ({@see storeConfig/store}) : { liveSessionId, tutor_busy_elsewhere, tutor_overrun }.
    liveRoomSignals: null,
    isTyping: false,
    liveParticipants: [],
    liveTutors: [],
    typingUser: null,
    inCallUsers: [],
    raisedHands: [],
    phoneRinging: [],
    todayLiveSessionLogs: [],
    timesSinceLastCall: [],
    conferenceToken: null,
    callPortal: false,
    visibleInvite: false,
    sound: null,
    callMode: null,
    activityFinalizedCount: null,
    isAudioMuted: false,
    isVideoMuted: false,
    // Les périphériques choisis pendant le test d'entrée, à reprendre au moment
    // d'ouvrir la salle. Survit volontairement à IS_LIVE_MODE_CHANGE — c'est
    // précisément à l'entrée en séance qu'ils servent — et n'est effacé qu'à la
    // sortie, par RESET_LIVE_MODE.
    precallDevices: { audioinput: null, videoinput: null },
    soundModeIds: [],
    outOfAppIds: [],
    activityCameraOnIds: [],
    voiceActivityRecordingIds: [],
    activeTime: [],
    liveReport: null,
    callKeepUUID: null,
    // L'appel système qui tient le live (CallKit, le service de premier plan d'Android) tourne vraiment.
    isCallKeepOn: false,
    // La salle attend-elle cet appel pour se connecter (`LiveKitRoomM`) ? Sous Android, faux quand il ne peut
    // pas tenir (micro pas accordé, démarrage refusé) : `engine/live/room/callKeep/callService`. Persistés comme
    // tout `liveMode` : un live tué laisse leur dernière valeur jusqu'à l'entrée suivante, qui les réécrit.
    hasPhonePermission: true,
    roomVersion: "",
    liveSessionsList: [],
    poorConnectionQualityIds: [],
    isLocalPoorConnection: false,
    agentNotifCounts: [],
    ongoingCheckpointIds: [],
    completedConversations: [],
    currentStudentActivity: {}, // [studentId]: activityId
    seenStudentActivity: {}, // [participant identity]: the activity that device shows (null = none)
    studentActivityFocusRequest: null, // { studentId, activityId, requestId }
    newTodoActivity: null,
    isGrayedOut: false,
    lastPopupTimes: {},
    showRaiseHandCheckpoint: {},
    isAgentModalVisible: false,
    forceActivityFocus: false,
    suggestedActivities: {}, // { [liveSessionId]: activityId }
    isSoundModeProcessing: false,
    conversationCounts: {}, //  tracks conversation counts for detecting new summaries
    checkpointConversation: null, // Current checkpoint conversation to show in overlay
    quizProgress: {}, // { [conversationId]: { answers: [], currentQuestionIndex: number } }
    // bandeau web mobile : état replié et affichage déjà mesuré, tous deux
    // remis à zéro avec le live
    liveWebMobileBannerCollapsed: false,
    liveWebMobileBannerShown: false,
    leftPanelCollapsed: false,
    rightPanelCollapsed: false,
    isFocusScreenShare: false,
    ongoingStartingBlockIds: {}, // { [liveSessionId]: { [studentId]: [convId, ...] } }
    autoMode: {
        // élève choisit seul son activité suivante en séance de groupe
        showEval: false,
        sourceActivity: null,
        selectedEval: null, // "got_it" | "mixed" | "lost"
        showChoiceModal: false,
        proposals: [],
        isLoading: false,
        error: null,
    },
    recentFinalizedActivities: {}, // { [studentId]: { activityId, finishedAt } }
    autoModeStartedActivities: {}, // { [studentId]: { activityId, evaluation } }
}

// Helper function to handle metrics data processing
const processMetricsData = (state, metricsData) => {
    if (!metricsData) {
        return {
            activeTime: state.activeTime,
            timesSinceLastCall: state.timesSinceLastCall,
            activeTimeCalculatedAt: state.activeTimeCalculatedAt,
            timesSinceLastCallCalculatedAt:
                state.timesSinceLastCallCalculatedAt,
        }
    }

    const now = new Date()
    const calculatedAt = metricsData.calculated_at
        ? new Date(metricsData.calculated_at)
        : now
    const timeDiffSeconds = Math.floor(
        (now.getTime() - calculatedAt.getTime()) / 1000
    )

    let newActiveTime = [...state.activeTime]
    let newTimesSinceLastCall = { ...state.timesSinceLastCall }

    // Handle active_time data
    if (metricsData.active_time) {
        Object.entries(metricsData.active_time).forEach(
            ([participantId, time]) => {
                if (time !== undefined) {
                    const adjustedTime =
                        parseInt(String(time)) + timeDiffSeconds
                    const participantIdInt = parseInt(participantId)
                    newActiveTime = newActiveTime.filter(
                        (existing) => existing.id !== participantIdInt
                    )
                    newActiveTime.push({
                        id: participantIdInt,
                        time: adjustedTime,
                    })
                }
            }
        )
    }

    // Handle times_since_last_call data - only for students NOT in sound mode and actually in the call
    if (metricsData.time_since_last_call) {
        Object.entries(metricsData.time_since_last_call).forEach(
            ([participantId, time]) => {
                const participantIdInt = parseInt(participantId)

                // Check if participant is actually in the live session
                const isInLiveSession =
                    state.liveParticipants.includes(participantIdInt)

                // Check if participant is in sound mode (from Redux state)
                const isInSoundMode =
                    state.soundModeIds?.includes(participantIdInt)

                if (time !== undefined && isInLiveSession && !isInSoundMode) {
                    // Apply time difference for participants not in sound mode
                    const adjustedTime =
                        parseInt(String(time)) + timeDiffSeconds
                    newTimesSinceLastCall[participantIdInt] = adjustedTime
                } else if (!isInLiveSession) {
                    newTimesSinceLastCall[participantIdInt] = parseInt(String(time))
                } else if (isInSoundMode) {
                    // If student is in sound mode or not in live session, reset their time to 0
                    newTimesSinceLastCall[participantIdInt] = 0
                } 
            }
        )
    }

    return {
        activeTime: newActiveTime,
        timesSinceLastCall: newTimesSinceLastCall,
        activeTimeCalculatedAt: now.toISOString(),
        timesSinceLastCallCalculatedAt: now.toISOString(),
    }
}

/** Les signaux de débordement de la salle `liveSessionId`, ou null. */
export function liveRoomSignalsFor(state, liveSessionId) {
    const signals = state.liveMode?.liveRoomSignals
    return liveSessionId && signals?.liveSessionId === liveSessionId ? signals : null
}

// What the tutor's summary popup can show: a conversation with no summary has
// nothing to show there, whoever sent it.
export const hasSummaryToShow = (conversation) => !!conversation?.summary?.trim()

// The single gate of `completedConversations`, the queue of that popup: one
// already queued is not queued twice.
const admitCompleted = (list, entry) => {
    const conversation = entry.conversation
    if (!hasSummaryToShow(conversation)) return list
    if (list.some((c) => c.conversation?.id === conversation.id)) return list
    return [entry, ...list].slice(0, 10)
}

const withTodayLog = (state, log) => ({
    ...state,
    todayLiveSessionLogs: [...(state?.todayLiveSessionLogs ?? []), log],
})

export default function liveMode(state = initialState, action) {
    switch (action.type) {
        case liveModeConstants.SET_POOR_CONNECTION_IDS:
            return {
                ...state,
                poorConnectionQualityIds: action.payload,
            }
        case liveModeConstants.ADD_POOR_CONNECTION_ID:
            return {
                ...state,
                poorConnectionQualityIds:
                    state.poorConnectionQualityIds?.includes(action.userId)
                        ? state.poorConnectionQualityIds
                        : [...state.poorConnectionQualityIds, action.userId],
            }
        case liveModeConstants.REMOVE_POOR_CONNECTION_ID:
            return {
                ...state,
                poorConnectionQualityIds: state.poorConnectionQualityIds.filter(
                    (id) => id !== action.userId
                ),
            }
        case liveModeConstants.SET_IS_LOCAL_POOR_CONNECTION:
            return {
                ...state,
                isLocalPoorConnection: action.value,
            }
        case liveService.getLiveSessions.name + apiConstants._SUCCESS:
            return {
                ...state,
                liveSessionsList: action.data,
            }
        case liveModeConstants.SET_CALL_KEEP_UUID:
            return {
                ...state,
                callKeepUUID: action.uuid,
            }
        case liveModeConstants.SET_CALLKEEP_ON:
            return {
                ...state,
                isCallKeepOn: action.newStatus,
            }
        case liveModeConstants.SET_CALL_STATE:
            return {
                ...state,
                isCallKeepOn: action.isCallKeepOn,
                hasPhonePermission: action.hasPhonePermission,
            }
        case liveModeConstants.SET_HAS_PHONE_PERMISSION:
            return {
                ...state,
                hasPhonePermission: action.newStatus,
            }
        case liveService.getTodayLiveReport.name + apiConstants._SUCCESS:
            return {
                ...state,
                liveReport: action.data,
            }
        case liveService.setSoundMode.name + apiConstants._REQUEST:
        case liveServiceV2.setSoundModeV2.name + apiConstants._REQUEST:
            return {
                ...state,
                isSoundModeProcessing: true,
            }
        case liveService.setSoundMode.name + apiConstants._SUCCESS:
            return {
                ...state,
                soundModeIds: action.data?.sound_mode_ids,
                todayLiveSessionLogs: action.data?.today_live_session_logs,
                isSoundModeProcessing: false,
            }
        case liveService.setSoundMode.name + apiConstants._FAILURE:
        case liveServiceV2.setSoundModeV2.name + apiConstants._FAILURE:
            return {
                ...state,
                isSoundModeProcessing: false,
                ...(action.error?.data?.sound_mode_ids !== undefined && {
                    soundModeIds: action.error.data.sound_mode_ids,
                }),
            }
        case liveModeConstants.SOUND_MODE:
            return {
                ...state,
                soundModeIds: action.newState
                    ? state.soundModeIds?.includes(action.userId)
                        ? state.soundModeIds
                        : [...state.soundModeIds, action.userId]
                    : state?.soundModeIds.filter((id) => id !== action.userId),
            }
        case liveModeConstants.PRECALL_DEVICES:
            return {
                ...state,
                precallDevices: { ...state.precallDevices, ...action.devices },
            }

        case liveModeConstants.OUT_OF_APP:
            return {
                ...state,
                outOfAppIds: action.newState
                    ? state.outOfAppIds?.includes(action.userId)
                        ? state.outOfAppIds
                        : [...state.outOfAppIds, action.userId]
                    : state?.outOfAppIds.filter((id) => id !== action.userId),
            }
        case liveModeConstants.ACTIVITY_CAMERA_ON:
            return {
                ...state,
                activityCameraOnIds: action.newState
                    ? state.activityCameraOnIds?.includes(action.userId)
                        ? state.activityCameraOnIds
                        : [...state.activityCameraOnIds, action.userId]
                    : state?.activityCameraOnIds.filter(
                          (id) => id !== action.userId
                      ),
            }
        case liveModeConstants.VOICE_RECORDING_ACTIVITY:
            return {
                ...state,
                voiceActivityRecordingIds: action.newState
                    ? state.voiceActivityRecordingIds?.includes(action.userId)
                        ? state.voiceActivityRecordingIds
                        : [...state.voiceActivityRecordingIds, action.userId]
                    : state?.voiceActivityRecordingIds.filter(
                          (id) => id !== action.userId
                      ),
            }
        case videoCallConstants.MUTE_AUDIO:
            return {
                ...state,
                isAudioMuted: action?.muted,
            }
        case videoCallConstants.MUTE_VIDEO:
            return {
                ...state,
                isVideoMuted: action?.muted,
            }
        case liveModeConstants.RESET_LIVE_MODE:
            return initialState
        // Clear the persisted student→activity map on logout so it never leaks
        // across an account switch (liveMode is in the redux-persist whitelist).
        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
            return {
                ...state,
                recentFinalizedActivities: {},
                autoModeStartedActivities: {},
                studentActivityFocusRequest: null,
                // Les signaux de la salle nomment des élèves : ils ne passent pas au compte suivant.
                liveRoomSignals: null,
                // Reset the auto-mode overlay too: it holds the previous user's
                // sourceActivity + generated proposals, and liveMode is
                // redux-persisted — a hard logout mid-live would otherwise leak
                // that content into the next account's storage.
                autoMode: initialState.autoMode,
                // Même raison, et le cas est concret : sur un portable
                // familial ou un poste d'école, les identifiants de
                // périphérique de l'élève précédent traverseraient — et
                // s'appliqueraient, un identifiant restant valide dans le même
                // profil navigateur d'un compte à l'autre.
                precallDevices: initialState.precallDevices,
            }
        case liveModeConstants.SET_RECENT_FINALIZED_ACTIVITY: {
            const { studentId, activityId, finishedAt } = action
            if (!studentId) return state
            return {
                ...state,
                recentFinalizedActivities: {
                    ...state.recentFinalizedActivities,
                    [studentId]: { activityId, finishedAt },
                },
            }
        }
        case liveModeConstants.CLEAR_RECENT_FINALIZED_ACTIVITY: {
            const { studentId } = action
            if (!studentId || !state.recentFinalizedActivities?.[studentId])
                return state
            const next = { ...state.recentFinalizedActivities }
            delete next[studentId]
            return { ...state, recentFinalizedActivities: next }
        }
        case liveModeConstants.SET_AUTO_MODE_STARTED_ACTIVITY: {
            const { studentId, evaluation, activityId } = action
            if (!studentId) return state
            return {
                ...state,
                autoModeStartedActivities: {
                    ...state.autoModeStartedActivities,
                    [studentId]: { evaluation, activityId },
                },
            }
        }
        case liveModeConstants.CLEAR_AUTO_MODE_STARTED_ACTIVITY: {
            const { studentId } = action
            if (!studentId || !state.autoModeStartedActivities?.[studentId])
                return state
            const next = { ...state.autoModeStartedActivities }
            delete next[studentId]
            return { ...state, autoModeStartedActivities: next }
        }
        case liveServiceV2.getLiveSessionV2.name + apiConstants._SUCCESS: {
            const liveSession = action.data.live_session
            const liveSessionId = liveSession?.id

            // Detect new summaries by comparing conversation counts
            // Exclude ongoing starting_block conversations to avoid false
            // notifications when the student hasn't finished the checkpoint yet
            const isOngoingStartingBlock = (conv) =>
                conv.status === "ongoing" && (
                    conv.trigger_context === "starting_block" ||
                    conv.trigger_context === "starting_block_study_program"
                )

            let updatedAgentNotifCounts = [...state.agentNotifCounts]

            if (liveSession?.users) {
                liveSession.users.forEach((user) => {
                    const studentId = user.id
                    const currentConversations = user.conversations || []
                    const countableConversations = currentConversations.filter(
                        (conv) => !isOngoingStartingBlock(conv)
                    )
                    const conversationCount = countableConversations.length

                    // Find existing notif count for this student
                    const existingNotif = state.agentNotifCounts.find(
                        (notif) =>
                            notif.liveSessionId === liveSessionId &&
                            notif.studentId === studentId
                    )

                    // Find previous conversation count (stored in a new field)
                    const previousCount = state.conversationCounts?.[liveSessionId]?.[studentId] || 0

                    // If there are new conversations, increment the notif count
                    if (conversationCount > previousCount) {
                        const newSummariesCount = conversationCount - previousCount

                        if (existingNotif) {
                            // Update existing notif count
                            updatedAgentNotifCounts = updatedAgentNotifCounts.map((notif) =>
                                notif.liveSessionId === liveSessionId &&
                                notif.studentId === studentId
                                    ? {
                                        ...notif,
                                        count: notif.count + newSummariesCount,
                                    }
                                    : notif
                            )
                        } else if (newSummariesCount > 0) {
                            // Create new notif count entry
                            updatedAgentNotifCounts.push({
                                liveSessionId,
                                studentId,
                                count: newSummariesCount,
                            })
                        }
                    }
                })
            }

            // Update conversation counts for future comparison
            // Exclude ongoing starting_block to match the counting logic above
            const updatedConversationCounts = {...state.conversationCounts}
            if (liveSession?.users) {
                if (!updatedConversationCounts[liveSessionId]) {
                    updatedConversationCounts[liveSessionId] = {}
                }
                liveSession.users.forEach((user) => {
                    updatedConversationCounts[liveSessionId][user.id] =
                        (user.conversations || []).filter(
                            (conv) => !isOngoingStartingBlock(conv)
                        ).length
                })
            }

            // Detect starting_block conversations that just completed
            // (were ongoing on previous poll, now have a terminal status)
            // so the tutor gets the popup even before the student joins LiveKit
            let updatedCompletedConversations = [...state.completedConversations]
            const updatedOngoingStartingBlockIds = {}

            if (liveSession?.users) {
                if (!updatedOngoingStartingBlockIds[liveSessionId]) {
                    updatedOngoingStartingBlockIds[liveSessionId] = {}
                }

                liveSession.users.forEach((user) => {
                    const studentId = user.id
                    const conversations = user.conversations || []

                    // Current ongoing starting_block IDs
                    const currentOngoingIds = conversations
                        .filter((conv) => isOngoingStartingBlock(conv))
                        .map((conv) => conv.id)

                    updatedOngoingStartingBlockIds[liveSessionId][studentId] = currentOngoingIds

                    // Previously tracked ongoing starting_block IDs
                    const prevOngoingIds =
                        state.ongoingStartingBlockIds?.[liveSessionId]?.[studentId] || []

                    // Find conversations that were ongoing but are no longer
                    prevOngoingIds.forEach((convId) => {
                        if (!currentOngoingIds.includes(convId)) {
                            const completedConv = conversations.find(
                                (c) => c.id === convId && c.status !== "ongoing"
                            )
                            if (!completedConv) return

                            updatedCompletedConversations = admitCompleted(
                                updatedCompletedConversations,
                                {
                                    liveSessionId,
                                    studentId,
                                    conversation: completedConv,
                                    timestamp: Date.now(),
                                    read: false,
                                }
                            )
                        }
                    })
                })
            }

            return {
                ...state,
                roomVersion: liveSession?.live_room?.version,
                liveRoomSignals: { liveSessionId, ...action.data?.live_signals },
                agentNotifCounts: updatedAgentNotifCounts,
                conversationCounts: updatedConversationCounts,
                completedConversations: updatedCompletedConversations,
                ongoingStartingBlockIds: updatedOngoingStartingBlockIds,
            }
        }
        case liveService.getLiveSession.name + apiConstants._SUCCESS:
            return {
                ...state,
                conferenceToken: action.data?.conference_token,
                screenshareToken: action.data?.screenshare_token,
            }
        case liveService.joinCall.name + apiConstants._SUCCESS:
        case liveServiceV2.joinCallV2.name + apiConstants._SUCCESS:
        case liveService.getCall.name + apiConstants._SUCCESS:
        case liveServiceV2.getCallV2.name + apiConstants._SUCCESS:
            return {
                ...state,
                inCallUsers: action.data?.call_participant_ids,
                raisedHands: action.data?.raised_hands_ids,
                visibleInvite: action.data?.has_incoming_call,
                todayLiveSessionLogs: action.data?.today_live_session_logs,
                soundModeIds: action.data?.sound_mode_ids,
                voiceActivityRecordingIds:
                    action.data?.voice_recording_activity_ids,
            }
        case liveService.raiseHand.name + apiConstants._SUCCESS:
        case liveService.lowerHands.name + apiConstants._SUCCESS:
        case liveServiceV2.lowerHandsV2.name + apiConstants._SUCCESS:
        case liveService.lowerAllHands.name + apiConstants._SUCCESS:
            return {
                ...state,
                inCallUsers: action.data?.call_participant_ids,
                raisedHands: action.data?.raised_hands_ids,
                visibleInvite: action.data?.has_incoming_call,
                todayLiveSessionLogs: action.data?.today_live_session_logs,
                // soundModeIds intentionally omitted — managed by LiveKit events only
                voiceActivityRecordingIds:
                    action.data?.voice_recording_activity_ids,
            }
        case liveModeConstants.SET_TIMES_SINCE_LAST_CALL:
            return {
                ...state,
                timesSinceLastCall: action.newState,
                timesSinceLastCallCalculatedAt: action.calculatedAt,
            }
        case liveModeConstants.SET_ACTIVE_TIME:
            return {
                ...state,
                activeTime: action.newState,
                activeTimeCalculatedAt: action.calculatedAt,
            }
        case liveModeConstants.NEW_LIVE_SESSION_LOG:
            return withTodayLog(state, action.newState)
        // Les journaux du jour ne sont relus qu'à l'entrée dans l'appel.
        case liveService.finalizeLiveSession.name + apiConstants._SUCCESS:
            return withTodayLog(state, {
                live_session_id: state?.liveSessionId,
                action: "finalized_live_session",
            })

        case liveModeConstants.HIDE_INCOMING_CALL:
            return {
                ...state,
                visibleInvite: false,
            }
        case liveModeConstants.SHOW_INCOMING_CALL:
            return {
                ...state,
                visibleInvite: true,
            }
        case liveModeConstants.IS_LIVE_MODE_CHANGE:
            return {
                ...state,
                isLiveModeEnabled: action.newState,
                liveSessionId: parseInt(action.liveSessionId),
                enteredOn: action.enteredOn ?? null,
                entryKey: action.entryKey ?? null,
                // Entrer dans un live, c'est un nouveau live : le rappel « installe
                // l'appli » repart déployé. RESET_LIVE_MODE le fait déjà à la
                // sortie ; le refaire à l'entrée rend la garantie indépendante du
                // chemin emprunté, puisque liveMode est persisté.
                liveWebMobileBannerCollapsed: false,
                liveWebMobileBannerShown: false,
            }
        case liveModeConstants.IS_TYPING:
            return {
                ...state,
                isTyping: action.newState,
            }
        case liveModeConstants.TYPING_USER:
            return {
                ...state,
                typingUser: action.newState,
            }
        case liveModeConstants.LIVE_PARTICIPANTS:
            return {
                ...state,
                liveParticipants: action.newState,
            }
        case liveModeConstants.ADD_PARTICIPANT:
            if (!state.liveParticipants.includes(action.newState))
                return {
                    ...state,
                    liveParticipants: [
                        ...state.liveParticipants,
                        action.newState,
                    ],
                }
            return state
        // case liveModeConstants.LIVE_TUTORS:
        //     if (!state.liveTutors.map(item => item.id).includes(action.newState.id))
        //         return {
        //             ...state,
        //             liveTutors: [...state.liveTutors, action.newState]
        //         }
        //     return state
        case liveModeConstants.REMOVE_PARTICIPANT:
            return {
                ...state,
                liveParticipants: state.liveParticipants.filter(
                    (p) => p !== action.newState
                ),
            }
        // case liveModeConstants.REMOVE_TUTOR:
        //     return {
        //         ...state,
        //         liveTutors: state.liveTutors.filter(t => t.id.toString() !== action.newState?.toString())
        //     }
        case liveModeConstants.CLEAR_MEMBERS:
            return {
                ...state,
                liveTutors: [],
                liveParticipants: [],
            }
        case liveModeConstants.IN_CALL_USERS:
            return {
                ...state,
                inCallUsers: action.newState,
            }
        case liveModeConstants.ADD_IN_CALL_USER:
            return {
                ...state,
                inCallUsers: state.inCallUsers?.includes(action.newState)
                    ? state.inCallUsers
                    : [...state.inCallUsers, action.newState],
            }

        case liveModeConstants.REMOVE_IN_CALL_USERS:
            return {
                ...state,
                inCallUsers: [
                    ...state.inCallUsers.filter((t) => t !== action.newState),
                ],
            }
        case liveModeConstants.PHONE_RINGING:
            return {
                ...state,
                phoneRinging: action.newState,
            }
        case liveModeConstants.RESET_PHONE_RINGING:
            return {
                ...state,
                phoneRinging: [],
            }
        case liveModeConstants.ADD_PHONE_RINGING:
            return {
                ...state,
                phoneRinging: state.phoneRinging?.includes(action.newState)
                    ? state.phoneRinging
                    : [...state.phoneRinging, action.newState],
            }

        case liveModeConstants.REMOVE_PHONE_RINGING:
            return {
                ...state,
                phoneRinging: [
                    ...state.phoneRinging.filter((t) => t !== action.newState),
                ],
            }
        case liveModeConstants.RAISED_HANDS:
            return {
                ...state,
                raisedHands: action.newState,
            }
        case liveModeConstants.ADD_RAISED_HAND:
            return {
                ...state,
                raisedHands: state.raisedHands?.includes(action.newState)
                    ? state.raisedHands
                    : [...state.raisedHands, action.newState],
            }
        case liveModeConstants.REMOVE_RAISED_HAND:
            return {
                ...state,
                raisedHands: [
                    ...state.raisedHands.filter((t) => t !== action.newState),
                ],
            }
        case liveModeConstants.SET_CALL_PORTAL:
            return {
                ...state,
                callPortal: action.newState,
                callMode: action?.mode,
            }
        case liveModeConstants.PLAY_SOUND:
            return {
                ...state,
                sound: action.sound,
            }

        // Actions consolidées pour l'agent et les conversations
        case liveModeConstants.UPDATE_AGENT_STATE: {
            const { actionType, data } = action.payload

            switch (actionType) {
                case "SET_NOTIF_COUNT": {
                    const { liveSessionId, studentId, count } = data
                    const existingIndex = state.agentNotifCounts.findIndex(
                        (item) =>
                            item.liveSessionId === liveSessionId &&
                            item.studentId === studentId
                    )

                    let updatedAgentNotifCounts

                    if (existingIndex !== -1) {
                        updatedAgentNotifCounts = state.agentNotifCounts.map(
                            (item, index) =>
                                index === existingIndex
                                    ? {
                                          ...item,
                                          count:
                                              count !== undefined
                                                  ? count
                                                  : item.count + 1,
                                      }
                                    : item
                        )
                    } else {
                        updatedAgentNotifCounts = [
                            ...state.agentNotifCounts,
                            {
                                liveSessionId,
                                studentId,
                                count: count !== undefined ? count : 1,
                            },
                        ]
                    }
                    return {
                        ...state,
                        agentNotifCounts: updatedAgentNotifCounts,
                    }
                }

                case "SET_ACTIVE_CONVERSATIONS": {
                    const { studentIds, operation } = data
                    let updatedIds

                    if (operation === "add") {
                        updatedIds = Array.from(
                            new Set([
                                ...state.ongoingCheckpointIds,
                                ...studentIds,
                            ])
                        )
                    } else if (operation === "remove") {
                        updatedIds = state.ongoingCheckpointIds.filter(
                            (id) => !studentIds.includes(id)
                        )
                    } else {
                        updatedIds = studentIds
                    }

                    return {
                        ...state,
                        ongoingCheckpointIds: updatedIds,
                    }
                }

                default:
                    return state
            }
        }
        case liveModeConstants.SET_AGENT_NOTIF_COUNTS: {
            const { liveSessionId, studentId, count } = action.payload
            const existingIndex = state.agentNotifCounts.findIndex(
                (item) =>
                    item.liveSessionId === liveSessionId &&
                    item.studentId === studentId
            )

            let updatedAgentNotifCounts

            if (existingIndex !== -1) {
                updatedAgentNotifCounts = state.agentNotifCounts.map(
                    (item, index) =>
                        index === existingIndex
                            ? {
                                  ...item,
                                  count:
                                      count !== undefined
                                          ? count
                                          : item.count + 1,
                              }
                            : item
                )
            } else {
                updatedAgentNotifCounts = [
                    ...state.agentNotifCounts,
                    {
                        liveSessionId,
                        studentId,
                        count: count !== undefined ? count : 1,
                    },
                ]
            }
            return {
                ...state,
                agentNotifCounts: updatedAgentNotifCounts,
            }
        }
        case liveModeConstants.SET_NEW_TODO_ACTIVITY:
            return {
                ...state,
                newTodoActivity: action.activity,
                forceActivityFocus: action.forceFocus || false,
            }
        case liveModeConstants.SET_CURRENT_STUDENT_ACTIVITY:
            return {
                ...state,
                currentStudentActivity: {
                    ...state.currentStudentActivity,
                    [action.studentId]: action.activity,
                },
            }
        case liveModeConstants.SET_SEEN_STUDENT_ACTIVITY:
            return {
                ...state,
                seenStudentActivity: Object.fromEntries(
                    Object.entries({
                        ...(action.replace ? {} : state.seenStudentActivity),
                        ...action.seen,
                    }).filter(([, activityId]) => activityId !== undefined)
                ),
            }
        case liveModeConstants.REQUEST_STUDENT_ACTIVITY_FOCUS:
            return {
                ...state,
                studentActivityFocusRequest: {
                    studentId: action.studentId,
                    activityId: action.activityId,
                    requestId:
                        (state.studentActivityFocusRequest?.requestId ?? 0) + 1,
                },
            }
        case liveModeConstants.SET_IS_GRAYED_OUT:
            return {
                ...state,
                isGrayedOut: action.newState,
            }

        case liveModeConstants.SET_LAST_POPUP_TIME:
            return {
                ...state,
                lastPopupTimes: {
                    ...state.lastPopupTimes,
                    [action.payload.sessionId]: {
                        ...(state.lastPopupTimes[action.payload.sessionId] ||
                            {}),
                        [action.payload.userId]: action.payload.timestamp,
                    },
                },
            }

        case liveModeConstants.SET_SHOW_RAISE_HAND_CHECKPOINT:
            return {
                ...state,
                showRaiseHandCheckpoint: {
                    ...state.showRaiseHandCheckpoint,
                    [action.payload.userId]: action.payload.show,
                },
            }

        case liveModeConstants.SET_IS_AGENT_MODAL_VISIBLE:
            return {
                ...state,
                isAgentModalVisible: action.newState,
            }

        case liveModeConstants.SET_MINI_MODE:
            return {
                ...state,
                miniMode: action.newState,
            }

        case liveModeConstants.SET_FOCUSED_MODE:
            return {
                ...state,
                focusedMode: action.newState,
            }
        
        case liveModeConstants.SET_CHECKPOINT_CONVERSATION:
            return {
                ...state,
                checkpointConversation: action.conversation,
            }

        case liveModeConstants.CLEAR_CHECKPOINT_CONVERSATION:
            return {
                ...state,
                checkpointConversation: null,
            }

        case liveModeConstants.SET_QUIZ_PROGRESS: {
            const { conversationId, answers, currentQuestionIndex } = action.payload
            return {
                ...state,
                quizProgress: {
                    ...state.quizProgress,
                    [conversationId]: { answers, currentQuestionIndex },
                },
            }
        }

        case liveModeConstants.CLEAR_QUIZ_PROGRESS: {
            const { conversationId } = action.payload
            const newQuizProgress = { ...state.quizProgress }
            delete newQuizProgress[conversationId]
            return {
                ...state,
                quizProgress: newQuizProgress,
            }
        }

        case liveService.createConversation.name + apiConstants._SUCCESS: {
            const context = action.data?.conversation?.trigger_context
            if (context === "starting_block" || context === "starting_block_study_program") {
                return state
            }
            return {
                ...state,
                checkpointConversation: action.data,
            }
        }

        case liveService.respondConversation.name + apiConstants._SUCCESS: {
            if (!state.checkpointConversation) {
                return state
            }
            const currentConversationId = state.checkpointConversation?.conversation?.id
            const responseConversationId = action.data?.conversation?.id
            if (currentConversationId && currentConversationId === responseConversationId) {
                return {
                    ...state,
                    checkpointConversation: action.data,
                }
            }
            return state
        }
        
        case liveModeConstants.UPDATE_CONVERSATION: {
            const { actionType: convActionType, data: convData } =
                action.payload

            switch (convActionType) {
                case "ADD_CONVERSATION": {
                    const newCompletedConversations = admitCompleted(
                        state.completedConversations,
                        {
                            liveSessionId: convData.liveSessionId,
                            studentId: convData.studentId,
                            conversation: convData.conversation?.conversation,
                            timestamp: convData.timestamp,
                            read: convData.read || false,
                        }
                    )
                    if (newCompletedConversations === state.completedConversations) {
                        return state
                    }

                    return {
                        ...state,
                        completedConversations: newCompletedConversations,
                    }
                }

                case "MARK_READ":
                    return {
                        ...state,
                        completedConversations:
                            state.completedConversations.filter(
                                (conv) =>
                                    !(
                                        conv.liveSessionId ===
                                            convData.liveSessionId &&
                                        conv.studentId === convData.studentId &&
                                        conv.conversation?.id ===
                                            convData.conversationId
                                    )
                            ),
                    }

                default:
                    return state
            }
        }
        case liveModeConstants.SET_SUGGESTED_ACTIVITY:
            return {
                ...state,
                suggestedActivities: {
                    ...state.suggestedActivities,
                    [action.liveSessionId]: action.activityId,
                },
            }
        case liveModeConstants.UPDATE_PARTICIPANT_ACTIVE_TIME: {
            const activeTimeNow = new Date()
            const activeTimeCalculatedAt = action.calculatedAt
                ? new Date(action.calculatedAt)
                : activeTimeNow
            const activeTimeTimeDiffSeconds = Math.floor(
                (activeTimeNow.getTime() - activeTimeCalculatedAt.getTime()) /
                    1000
            )
            const adjustedActiveTime =
                action.activeTime + activeTimeTimeDiffSeconds

            return {
                ...state,
                activeTime: state.activeTime
                    .filter((item) => item.id !== action.participantId)
                    .concat([
                        {
                            id: action.participantId,
                            time: adjustedActiveTime,
                        },
                    ]),
                activeTimeCalculatedAt: activeTimeNow.toISOString(),
            }
        }
        case liveModeConstants.UPDATE_PARTICIPANT_TIMES_SINCE_LAST_CALL: {
            const timesSinceLastCallNow = new Date()
            const timesSinceLastCallCalculatedAt = action.calculatedAt
                ? new Date(action.calculatedAt)
                : timesSinceLastCallNow
            const timesSinceLastCallTimeDiffSeconds = Math.floor(
                (timesSinceLastCallNow.getTime() -
                    timesSinceLastCallCalculatedAt.getTime()) /
                    1000
            )

            const isInSoundMode = state.soundModeIds?.includes(
                action.participantId
            )
            const isInLiveSession = state.liveParticipants.includes(
                action.participantId
            )

            const adjustedTimesSinceLastCall =
                (isInLiveSession && !isInSoundMode)
                    ? (action.timesSinceLastCall +
                      timesSinceLastCallTimeDiffSeconds)
                    : 0

            return {
                ...state,
                timesSinceLastCall: {
                    ...state.timesSinceLastCall,
                    [action.participantId]: adjustedTimesSinceLastCall,
                },
                timesSinceLastCallCalculatedAt:
                    timesSinceLastCallNow.toISOString(),
            }
        }

        case liveServiceV2.setSoundModeV2.name + apiConstants._SUCCESS: {
            const metricsData = action.data.metrics
            const processedMetrics = processMetricsData(state, metricsData)

            return {
                ...state,
                ...processedMetrics,
                soundModeIds: action.data?.sound_mode_ids,
                isSoundModeProcessing: false,
            }
        }
        case liveServiceV2.getMetricsV2.name + apiConstants._SUCCESS: {
            const metricsData = action.data.metrics
            const processedMetrics = processMetricsData(state, metricsData)

            return {
                ...state,
                ...processedMetrics,
            }
        }
        case liveModeConstants.SET_LIVE_WEB_MOBILE_BANNER_SHOWN:
            return {
                ...state,
                liveWebMobileBannerShown: true,
            }
        case liveModeConstants.SET_LIVE_WEB_MOBILE_BANNER_COLLAPSED:
            return {
                ...state,
                liveWebMobileBannerCollapsed: action.collapsed,
            }
        case liveModeConstants.SET_LEFT_PANEL_COLLAPSED:
            return {
                ...state,
                leftPanelCollapsed: action.collapsed,
            }
        case liveModeConstants.SET_RIGHT_PANEL_COLLAPSED:
            return {
                ...state,
                rightPanelCollapsed: action.collapsed,
            }
        case liveModeConstants.SET_FOCUS_SCREEN_SHARE:
            return {
                ...state,
                isFocusScreenShare: action.isFocus,
            }
        case liveModeConstants.BATCH_UPDATE_TIMERS: {
            const batchNow = new Date()
            const batchCalculatedAt = action.calculatedAt
                ? new Date(action.calculatedAt)
                : batchNow
            const batchTimeDiffSeconds = Math.floor(
                (batchNow.getTime() - batchCalculatedAt.getTime()) / 1000
            )

            // Apply all timesSinceLastCall updates at once
            let newTimesSinceLastCall = { ...state.timesSinceLastCall }
            if (action.timesSinceLastCallUpdates) {
                for (const { participantId, timesSinceLastCall } of action.timesSinceLastCallUpdates) {
                    const isInSoundMode = state.soundModeIds?.includes(participantId)
                    const isInLiveSession = state.liveParticipants.includes(participantId)
                    newTimesSinceLastCall[participantId] =
                        (isInLiveSession && !isInSoundMode)
                            ? timesSinceLastCall + batchTimeDiffSeconds
                            : 0
                }
            }

            // Apply all activeTime updates at once
            let newActiveTime = [...state.activeTime]
            if (action.activeTimeUpdates) {
                for (const { participantId, activeTime } of action.activeTimeUpdates) {
                    newActiveTime = newActiveTime.filter(
                        (item) => item.id !== participantId
                    )
                    newActiveTime.push({
                        id: participantId,
                        time: activeTime + batchTimeDiffSeconds,
                    })
                }
            }

            return {
                ...state,
                timesSinceLastCall: newTimesSinceLastCall,
                timesSinceLastCallCalculatedAt: batchNow.toISOString(),
                activeTime: newActiveTime,
                activeTimeCalculatedAt: batchNow.toISOString(),
            }
        }
        case liveModeConstants.AUTO_MODE_OPEN_EVAL:
            return {
                ...state,
                autoMode: {
                    ...state.autoMode,
                    showEval: true,
                    sourceActivity: action.sourceActivity,
                    selectedEval: null,
                    showChoiceModal: false,
                    proposals: [],
                    isLoading: false,
                    error: null,
                },
            }
        case liveModeConstants.AUTO_MODE_SET_EVAL:
            return {
                ...state,
                autoMode: {
                    ...state.autoMode,
                    selectedEval: action.selectedEval,
                    showEval: false,
                    showChoiceModal: true,
                },
            }
        case liveModeConstants.AUTO_MODE_ADD_PROPOSAL:
            return {
                ...state,
                autoMode: {
                    ...state.autoMode,
                    proposals: action.proposal
                        ? [...state.autoMode.proposals, action.proposal]
                        : state.autoMode.proposals,
                },
            }
        case liveModeConstants.AUTO_MODE_SET_LOADING:
            return {
                ...state,
                autoMode: {
                    ...state.autoMode,
                    isLoading: action.isLoading,
                },
            }
        case liveModeConstants.AUTO_MODE_SET_ERROR:
            return {
                ...state,
                autoMode: {
                    ...state.autoMode,
                    error: action.error,
                    isLoading: false,
                },
            }
        case liveModeConstants.AUTO_MODE_CLOSE:
            return {
                ...state,
                autoMode: {
                    ...initialState.autoMode,
                },
            }
        default:
            return state
    }
}
